import { SoftDeleteInfo } from "@/types/deleteRequest";
import {KindergartenActReconciliationStatus} from "@/enums/Kindergarten";
import {CalculationReportInfo, Report} from "@/types/report";

export class OwnershipType {
  id: number;
  name: string;
  code: number;

  constructor(id: number, name: string, code: number) {
    this.id = id;
    this.name = name;
    this.code = code;
  }
}

export interface LmsSystemItem {
  id: number;
  lmsId: string;
  kindergartenName: string;
  lmsName: string | null;
  regionGroup: RegionGroup | null;
  localityType: LocalityType | null;
  regionType: RegionType | null;
  kato: Kato | null;
  address: string | null;
  contractNumberAndDate: string | null;
  childrenCapacity: number | null;
  status: string;
  kindergartenAllowances: KindergartenAllowanceItem[] | [];
  kindergartenDocumentInfo: KindergartenDocumentInfo | null;
  bankBic: string | null;
  bankBin: string | null;
  iik: string | null;
}

export interface Kato {
  code: number;
  kindCode: number;
  nameKz: string;
  nameRu: string;
  fullNameKz: string;
  fullNameRu: string;
}

export class KindergartenSearchParams {
  pageSize: number = 10;
  page: number = 0;
  doName: string | null = null;
  bin: string | null = null;
  katoCode: number | null = null;
  status: string | null = "";
}

export class KindergartenDocumentInfo {
  id: number | null = null;
  bin: string | null = null;
  name: string | null = null;
  bankBic: string | null = null;
  bankBin: string | null = null;
  kbe: string | null = null;
  iik: string | null = null;
  ownershipType: OwnershipType | null = null;
  lmsSystems: LmsSystemItem[] = [];
  constructor(bin: string, name: string, bankBic: string, bankBin: string, kbe: string, iik: string, ownershipType: OwnershipType);
  constructor(bin?: string, name?: string, bankBic?: string, bankBin?: string, kbe?: string, iik?: string, ownershipType?: OwnershipType) {
    if (bin !== undefined && name !== undefined && bankBic !== undefined && bankBin !== undefined && kbe !== undefined && iik !== undefined && ownershipType !== undefined) {
      this.bin = bin;
      this.name = name;
      this.bankBic = bankBic;
      this.bankBin = bankBin;
      this.kbe = kbe;
      this.iik = iik;
      this.ownershipType = ownershipType;
    }

  };
}

export interface RegionGroup {
  id: number;
  code: number;
}

export interface LocalityType {
  id: number;
  name: string;
  code: number;
}

export interface RegionType {
  id: number;
  name: string;
  code: number;
}

export interface EducationalType {
  id: number;
  name: string;
  code: number;
}

export interface DurationTimeType {
  id: number;
  name: string;
  code: number;
}

export interface TimesheetType {
  id: number;
  name: string;
  code: number;
}

export interface KindergartenAllowanceItem {
  id: number;
  educationalType: EducationalType;
  durationTime: DurationTimeType;
  allowanceAmount: number;
  lmsSystem: LmsSystemItem | null;
}

export interface KindergartenPaidAmount {
  id: number | null;
  paidDate: Date | null;
  paidAmount: number;
  kindergartenCalculationId: number;
  paymentNumber: string | null;
}

export interface KindergartenTimesheetData {
  deletion?: SoftDeleteInfo | null;
  monthlyTimesheetId: string;
  bin: string;
  name: string;
  lmsId: string;
  lmsName: string;
  regionGroup: number;
  localityType: string;
  regionType: string;
  ownershipType: string;

  lmsDocumentId: string;
  childIin: string;
  passportNumber: string;
  childFirstName: string;
  childLastName: string;
  childMiddleName: string;
  parentIin: string;
  parentFirstName: string;
  parentLastName: string;
  parentMiddleName: string;
  phoneNumber: string;
  voucherType: string;
  escrowAccount: string;

  monthlyTimesheetDatasetId: string;
  lmsChildId: string;
  arrivedDate: string;
  departureDate: string;
  durationTime: string;
  educationalType: string;
  accountingMonth: number;
  accountingYear: number;
  timesheetType: string;
  totalDaysOnMonth: number;
  workingDays: number;
  weekendDays: number;
  excusedAbsenceDays: number;
  unexcusedAbsenceDays: number;
  presenceDays: number;
  planningDays: number;
  sickLeaveDays: number;
  haveSickLeave: boolean;
  matchSickLeavePeriod: boolean;
  vacationDays: number;
  isVacationStatement: boolean;
  matchVacationStatement: boolean;
  recalculationAmount: number;
  totalToAccrueLms: number;
  preCalculationAmount: number;
  preReconciliationSum: number;
  errorText: string;

  isAgreed: boolean;
  consentDate: string;
  approverIin: string;
  approverFirstName: string;
  approverLastName: string;
  approverMiddleName: string;
  absenceRedLine: number;
  seats: number;
}

export interface KindergartenTimesheetGeneralData {
  id: string;
  monthlyTimesheetId: string;
  bin: string;
  kindergartenName: string;
  lmsId: string;
  lmsName: string;

  accountingMonth: number;
  accountingYear: number;

  totalDaysInMonth: number;
  workingDays: number;
  weekendDays: number;
  excusedAbsenceDays: number;
  unexcusedAbsenceDays: number;
  presenceDays: number;
  sickLeaveDays: number;
  vacationDays: number;
  planningDays: number;
  totalVisitDays: number;
  recalculationAmount: number;
  retentionAmount: number;
  plannedToAccrue: number;
  totalToAccrue: number;
  actuallyPaidAmount: number;
  sendDate: string;
}


export interface KindergartenActReconciliation {
  id: number | null;
  status: KindergartenActReconciliationStatus;
  report: Report;
  accountingYear: number;
  lmsId: string;
  comment: string;
}

export interface KindergartenRetention {
  id: number | null;
  lmsSystem: LmsSystemItem | null,
  lmsId: string | null;
  bin: string | null;
  accountingMonth: number;
  accountingYear: number;
  price: number;
  deletion?: SoftDeleteInfo | null;
}

export interface AllowanceToSignResponse {
  allowanceToSign: boolean;
  textProhibitionToSign: boolean;
}

export class KindergartenAnalyticsSearchParam {
  accountingMonth: number | null = null;
  accountingYear: number | null = null;
  katoCode: number | null = null;
}

export interface KindergartenAnalyticsResponse {
  katoNameRu: string;
  bin: string;
  lmsId: string;
  kindergartenName: string;
  hasPlanTimesheet: boolean;
  hasFactTimesheet: boolean;
  hasPDFForSign: boolean;
  isSigned: boolean;
  status: string;
}

export interface KindergartenSpecialStandard {
  kato: Kato
  localityType: LocalityType,
  price: number,
  year: number,
}